"use client";
import type {
  AuthenticatedUser,
  GlobalSearchResourceItem,
  GlobalSearchUserResult,
  Office
} from "@gettactic/api";
import { MARIN_ROUTES } from "@gettactic/defines/src/web-routes";
import { buildUrl } from "@gettactic/helpers";
import { useOrgFeatureFlag } from "@gettactic/hooks";
import { useMemo } from "react";
import { STARTERS } from "@/features/reporting/components/StarterQuestions";
import { canAccessAnalytics } from "@/features/reporting/lib/access";
import { storyParam } from "@/features/reporting/lib/story";
import type { Setting } from "../constants";
import { QUICK_ACTIONS, SETTINGS } from "../constants";
import { searchOffices, searchSettings } from "../searchUtils";

const MIN_QUESTION_LENGTH = 3;

/**
 * Reporting rows for what was typed: starter questions whose name matches,
 * and the typed text itself as a question for Explore to turn into a chart.
 */
const reportSuggestionsFor = (query: string): Setting[] => {
  const text = query.trim();
  if (text.length < MIN_QUESTION_LENGTH) {
    return [];
  }
  const reportsPath = buildUrl(MARIN_ROUTES.analytics);
  const lower = text.toLowerCase();
  const starters = STARTERS.filter((s) =>
    s.label.toLowerCase().includes(lower)
  ).map(
    (s): Setting => ({
      id: `s-report-starter-${s.label}`,
      type: "setting",
      name: s.label,
      description: "Open this report in Explore",
      url: `${reportsPath}${storyParam({ query: s.query, records: null, form: "auto", trail: [] })}`
    })
  );
  return [
    ...starters,
    {
      id: "s-report-ask",
      type: "setting",
      name: `Ask the reports: "${text}"`,
      description: "Turn it into a chart in Explore",
      url: `${reportsPath}?ask=${encodeURIComponent(text)}#tab-explore`
    }
  ];
};

interface UseSearchResultsParams {
  query: string;
  resources: GlobalSearchResourceItem[];
  people: GlobalSearchUserResult[];
  offices: Office[];
  hasNoApiResults: boolean;
  isAdmin: boolean;
  authenticatedUser: AuthenticatedUser;
}

export function useSearchResults({
  query,
  resources,
  people,
  offices,
  hasNoApiResults,
  isAdmin,
  authenticatedUser
}: UseSearchResultsParams) {
  const isReportingEnabled = useOrgFeatureFlag("reporting") === true;
  const roles = authenticatedUser?.user?.organization_user?.roles;
  const showsReports =
    isReportingEnabled && canAccessAnalytics(authenticatedUser);
  const filteredSettings = useMemo((): Setting[] => {
    const visible = SETTINGS.filter((s) => (s.isAdmin ? isAdmin : true))
      .filter((s) => (s.id.startsWith("s-reports-") ? showsReports : true))
      .filter((s) => (s.isAvailable ? s.isAvailable(authenticatedUser) : true));
    const results = searchSettings(visible, query);
    return results.map((r) => r.item as Setting);
    // Roles decide the report rows; the rest of the user does not matter here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, isAdmin, showsReports, roles]);

  const filteredActions = useMemo((): Setting[] => {
    const results = searchSettings(QUICK_ACTIONS, query);
    return results
      .map((r) => r.item as Setting)
      .filter((s) => (s.isAvailable ? s.isAvailable(authenticatedUser) : true));
  }, [
    query,

    // these two are needed for the isAvailable check
    // whole authenticatedUser is passed instead for easier typing
    authenticatedUser?.organization?.desk_bookings_enabled,
    authenticatedUser?.organization?.meeting_room_bookings_enabled
  ]);

  const filteredOffices = useMemo((): Office[] => {
    const results = searchOffices(offices, query);
    return results.map((r) => r.item) as Office[];
  }, [offices, query]);

  const reportSuggestions = useMemo(
    (): Setting[] => (showsReports ? reportSuggestionsFor(query) : []),
    [showsReports, query]
  );

  const hasNoResults = useMemo(() => {
    return (
      hasNoApiResults &&
      filteredActions.length === 0 &&
      filteredSettings.length === 0 &&
      resources.length === 0 &&
      people.length === 0 &&
      filteredOffices.length === 0
    );
  }, [
    hasNoApiResults,
    filteredActions.length,
    filteredSettings.length,
    resources.length,
    people.length,
    filteredOffices.length
  ]);

  // Report suggestions are reachable with the keyboard but never count as a
  // result: with nothing else found, Enter still asks Tessa.
  const totalResults = useMemo(() => {
    return (
      filteredActions.length +
      filteredSettings.length +
      resources.length +
      people.length +
      filteredOffices.length +
      (hasNoResults ? 0 : reportSuggestions.length)
    );
  }, [
    filteredActions.length,
    filteredSettings.length,
    resources.length,
    people.length,
    filteredOffices.length,
    reportSuggestions.length,
    hasNoResults
  ]);

  return {
    filteredSettings,
    filteredActions,
    filteredOffices,
    reportSuggestions,
    hasNoResults,
    totalResults
  };
}
